<template>
  <v-container fluid :class="{ 'mt-4': !$vuetify.breakpoint.mobile }" :style="$vuetify.breakpoint.mobile ? 'padding-bottom: 96px' : ''">
    <v-row align="center" class="mb-3">

      <v-col cols="12" md="">
        <v-text-field
          :placeholder="$t('rights.search')"
          :disabled="portionLoading"
          @keydown.enter.prevent="doSearch"
          @click:append="doSearch"
          v-model="searchQuery"
          append-icon="mdi-magnify"
          filled
          rounded
          hide-details
          class="t-search-query mb-0" />
      </v-col>

      <v-col v-if="!$vuetify.breakpoint.mobile" class="d-flex flex-wrap justify-end align-center py-0 pr-0">
        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="menuScope">
            <v-tooltip top>
              <template v-slot:activator="tooltipScope">
                <v-btn v-on="{ ...menuScope.on, ...tooltipScope.on }" :disabled="portionLoading" class="mx-1 my-1 tonal-button">
                  <v-icon left>mdi-dots-vertical</v-icon>
                  <span>{{ $t('rights.columnsButton') }}</span>
                </v-btn>
              </template>
              <span>{{ $t('rights.columnsButtonTooltip') }}</span>
            </v-tooltip>
          </template>

          <v-list dense>
            <v-list-item v-for="col in additionalColumns" :key="col.value">
              <v-list-item-action>
                <v-checkbox
                  :input-value="selectedColumns.includes(col.value)"
                  @change="toggleColumnSelect(col.value)"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                {{ col.text }}
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn v-on="tooltipScope.on" :disabled="portionLoading || downloading" class="mx-1 my-1" color="primary" @click="downloadRights">
              <v-icon v-if="downloading" left>mdi-loading mdi-spin</v-icon>
              <v-icon v-else left>mdi-tray-arrow-down</v-icon>
              <span>{{ $t('rights.download') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.downloadTooltip') }}</span>
        </v-tooltip>

        <v-tooltip top>
          <template v-slot:activator="tooltipScope">
            <v-btn outlined v-on="tooltipScope.on" class="mx-1 my-1" :disabled="portionLoading || checkedItems.length === 0" @click="onAddRights">
              <v-icon left>mdi-plus-circle-outline</v-icon>
              <span>{{ $t('rights.add') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('rights.add') }}</span>
        </v-tooltip>

      </v-col>

    </v-row>
    <mobile-data-list
      v-if="$vuetify.breakpoint.mobile"
      v-model="checkedItems"
      :items="mobileItems"
      :loading="portionLoading"
      :items-per-page="25"
      :footer-props="{ itemsPerPageOptions: [] }"
      item-key="rowNumber"
      :headers="mobileHeaders"
      show-select
      single-expand
    >
      <template #item.type="{ item }">
        <v-icon v-if="String(item.type) === '1'" small>mdi-volume-high</v-icon>
        <v-icon v-else-if="String(item.type) === '2'" small>mdi-video</v-icon>
        <span v-else>{{ item.type }}</span>
      </template>
    </mobile-data-list>

    <RightsTable
      v-else
      :items="rightsList"
      :checked-items.sync="checkedItems"
      :displayed-columns="displayedColumns"
      :continuos-amount="!unclaimedRights.finished"
      :portion-loading="portionLoading"
      :total-count="unclaimedRights.totalCount"
      @load-more="loadMoreItems"
      columnSortOrderStore="unclaimedRightsColumnsOrder"
    />

    <div v-if="$vuetify.breakpoint.mobile" class="ur-mobile-actions">
      <v-btn color="primary" :disabled="portionLoading || downloading" @click="downloadRights">
        <v-icon v-if="downloading" left>mdi-loading mdi-spin</v-icon>
        <v-icon v-else left>mdi-tray-arrow-down</v-icon>
        {{ $t('rights.download') }}
      </v-btn>
      <v-btn outlined :disabled="portionLoading || checkedItems.length === 0" @click="onAddRights">
        <v-icon left>mdi-plus-circle-outline</v-icon>
        {{ $t('rights.add') }}
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import RightsTable from "@/components/rights/RightsTable";
import MobileDataList from "@/components/common/MobileDataList";
import {mapGetters} from "vuex";
import DebounceSearchLauncherMixin from "@/components/mixins/DebounceSearchLauncherMixin";
import RightsholderInitedDataDrivenMixin from "@/components/mixins/RightsholderInitedDataDrivenMixin";
import SelectedColumnsStore from "@/components/mixins/SelectedColumnsStoreMixin";
import downloadFile from "@/utils/downloadFile";

export default {
  name: 'UnclaimedRights',
  components: { RightsTable, MobileDataList },
  data() {
    return {
      selectedColumns: [],
      selectedColumnsStore: `unclaimedRightsColumns-${this.$auth.user.name}`,
      portionLoading: false,
      addRightVisible: false,
      checkedItems: [],
      downloading: false
    }
  },

  mixins: [SelectedColumnsStore, DebounceSearchLauncherMixin, RightsholderInitedDataDrivenMixin],

  computed: {
    ...mapGetters('app', ['unclaimedRights']),

    rightsList() {
      if (!this.unclaimedRights.list) {
        return [];
      }
      return this.unclaimedRights.list.concat(this.unclaimedRights.finished ? [] : [null]);
    },

    additionalColumns() {
      const getCol = (value) => ({value, text: this.$t(`rights.columns.${value}`)})
      return ['rowNumber', 'spnr', 'version', 'year', 'labelName'].map(getCol);
    },

    allColumns() {
      return [
        'rowNumber',
        'type',
        'isrc',
        'spnr',
        'artistName',
        'title',
        'countryCode',
        'year',
        'labelName',
        'status',
        'version',
        'added'
      ];
    },

    displayedColumns() {
      return this.allColumns.filter((col) => (this.additionalColumns.some(({value}) => value === col) ? this.selectedColumns.includes(col) : true));
    },

    mobileItems() {
      return this.rightsList.filter(Boolean);
    },

    mobileHeaders() {
      const h = (value) => ({ value, text: this.$t(`rights.columns.${value}`) });
      const pinned = { title: 'primary', artistName: 'secondary', isrc: 'tertiary' };
      return this.allColumns.map(value => ({
        ...h(value),
        ...(pinned[value] ? { mobile: pinned[value] } : {}),
      }));
    },
  },

  methods: {

    // Override
    reloadItems() {
      this.loadRights();
    },

    // Do search
    doSearch() {
      this.loadRights();
    },

    // Override
    searchQueryUpdated() {
      // this.loadRights();
    },

    loadRights() {
      if (!this.portionLoading === true) {
        this.portionLoading = true;
        this.$store.dispatch('app/loadUnclaimedRightsList', {reload: true, search: this.searchQuery})
          .catch((err) => {
            this.$toast(err.message);
          })
          .finally(() => {
            this.portionLoading = false;
          });
      }
      return Promise.resolve('Already Loading')

    },

    loadMoreItems() {
      this.portionLoading = true;
      this.$store.dispatch('app/loadUnclaimedRightsList', {search: this.searchQuery})
        .catch((err) => {
          this.$toast(err.message);
        })
        .finally(() => {
          this.portionLoading = false;
        })
    },

    toggleColumnSelect(col) {
      if (this.selectedColumns.includes(col)) {
        this.selectedColumns = this.selectedColumns.filter(c => c !== col);
      } else {
        this.selectedColumns.push(col);
      }
    },

    downloadRights() {
      this.downloading = true
      this.$store.dispatch('app/getUnclaimedRightsDownloadUrl', {search: this.searchQuery, format: 'xlsx'})
        .then((res) => {
          this.downloading = false
          downloadFile((res.filename ? res.filename : 'rights.xlsx'), res.url);
        })
    },

    onAddRights() {
      this.$store.dispatch('app/addRightsToStaging', this.checkedItems)
        .then(() => {
          this.$toast(this.$t('rights.added_to_staging'));
          this.checkedItems = [];
        });
    }
  }
}
</script>

<style scoped>
@media (max-width: 600px) {
  .container {
    padding-bottom: 96px;
  }
}
.ur-mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px 16px 16px;
  background: linear-gradient(to bottom, transparent, var(--color-viewport-bg) 40%);
  z-index: 5;
  display: flex;
  gap: 8px;
}
.ur-mobile-actions .v-btn:first-child {
  flex: 1;
}
</style>